// lineChart.ts

type Padding = { top: number; right: number; bottom: number; left: number }

export interface Series {
	name: string
	color: string // CSS color
	data: any
}

export interface ChartConfig {
	width?: number
	height?: number
	padding?: Padding
	title?: string
	xLabel?: string
	yLabel?: string
	series: Series[] // ex) [{ name, color, data }]
	gridLines?: number // horizontal grid count
	showArea?: boolean // fill under lines
	curve?: 'linear' | 'smooth'
	smoothness?: number
}

export function renderScatterChart(mountTarget: string | HTMLElement, cfg: ChartConfig): void {
	const mount = typeof mountTarget === 'string' ? document.querySelector<HTMLElement>(mountTarget) : mountTarget
	if (!mount) throw new Error('Mount element not found')

	const {
		width = 1100,
		height = 600,
		padding = { top: 40, right: 40, bottom: 60, left: 46 },
		title = 'Line chart name',
		xLabel = '(x축 레이블)',
		yLabel = '(y축 레이블)',
		series,
		gridLines = 6,
	} = cfg

	// 초기화
	mount.innerHTML = ''
	mount.style.position = 'relative'

	const svgNS = 'http://www.w3.org/2000/svg'
	const svg = createEl('svg', {
		width: String(width),
		height: String(height),
		viewBox: `0 0 ${width} ${height}`,
		style: 'background:#fff;border-radius:4px;',
	})

	const root = createEl('g')
	svg.appendChild(root)

	// 제목
	root.appendChild(
		createEl(
			'text',
			{
				x: '20',
				y: '26',
				fill: '#6B6E79',
				'font-size': '20',
				'font-weight': '700',
				'font-family': 'ui-sans-serif, -apple-system, "Noto Sans KR", Arial',
			},
			title,
		),
	)

	// 플롯 영역
	const plotX = padding.left
	const plotY = padding.top
	const plotW = width - padding.left - padding.right
	const plotH = height - padding.top - padding.bottom

	const rectHeight = plotY + plotH + 50

	// 스케일
	const all = series.flatMap(s => s.data)
	const xMaxData = Math.max(...all.map(item => item.x), 1)
	const xMax = niceMax(xMaxData)
	const xMin = 0
	const yMaxData = Math.max(...all.map(item => item.y), 1)
	const yMax = niceMax(yMaxData)
	const yMin = 0

	const xCount = Math.max(series.length, 1)
	const xStep = plotW / Math.max(gridLines, 1)
	const xScale = (i: number) => plotX + i * xStep
	const yScale = (v: number) => plotY + 65 + (yMax - v) * ((plotH - 60) / (yMax - yMin))
	const xPointerScale = (v: number) => {
		return plotX + v * (plotW / (xMax - xMin))
	}
	const yPointerScale = (v: number) => {
		return plotY + 65 + (yMax - v) * ((plotH - 60) / (yMax - yMin))
	}

	// 수평 그리드 + y축 눈금
	const effectiveYGridLines = Math.min(gridLines, yMax)
	const grid = createEl('g')
	root.appendChild(grid)
	for (let i = 0; i <= effectiveYGridLines; i++) {
		const v = yMax * (i / effectiveYGridLines)
		const y = yScale(v)
		grid.appendChild(
			createEl('line', {
				x1: String(plotX),
				y1: String(y),
				x2: String(plotX + plotW),
				y2: String(y),
				stroke: '#DDE1E7',
				'stroke-dasharray': '4 4',
			}),
		)
		grid.appendChild(
			createEl(
				'text',
				{
					x: String(plotX - 10),
					y: String(y + 5),
					'text-anchor': 'end',
					fill: '#6B6E79',
					'font-size': '12',
					'font-family': 'ui-sans-serif, "Noto Sans KR", Arial',
				},
				String(Math.round(v)),
			),
		)
	}

	// 라벨
	root.appendChild(
		createEl(
			'text',
			{
				x: String(20),
				y: String(plotY + 48),
				fill: '#6B6E79',
				'font-size': '12',
				'font-weight': '600',
			},
			yLabel,
		),
	)

	root.appendChild(
		createEl(
			'text',
			{
				x: String(plotX + plotW),
				y: String(rectHeight),
				'text-anchor': 'end',
				fill: '#6B6E79',
				'font-size': '12',
				'font-weight': '600',
			},
			xLabel,
		),
	)

	// x축 카테고리 + 세로 보조선
	const xTicks = createEl('g')
	root.appendChild(xTicks)
	const effectiveXGridLines = Math.min(gridLines, xMax)
	for (let i = 0; i <= effectiveXGridLines; i++) {
		const v = xMax * (i / effectiveXGridLines)
		const x = xScale(i)
		xTicks.appendChild(
			createEl('line', {
				x1: String(x),
				y1: String(plotY + 65),
				x2: String(x),
				y2: String(rectHeight - 45),
				stroke: '#DDE1E7',
				'stroke-dasharray': '4 4',
			}),
		)
		xTicks.appendChild(
			createEl(
				'text',
				{
					x: String(x),
					y: String(plotY + plotH + 30),
					'text-anchor': 'middle',
					fill: '#111827',
					'font-size': '12',
					'font-family': 'ui-sans-serif, "Noto Sans KR", Arial',
				},
				String(Math.round(v)),
			),
		)
	}

	// 범례
	const legend = createEl('g')
	root.appendChild(legend)
	let lx = 20
	const ly = 48
	series.forEach((s, idx) => {
		const group = createEl('g')
		legend.appendChild(group)
		group.appendChild(
			createEl('rect', {
				x: String(lx),
				y: String(ly - 10),
				width: '14',
				height: '14',
				rx: '3',
				fill: s.color,
			}),
		)
		group.appendChild(
			createEl(
				'text',
				{
					x: String(lx + 20),
					y: String(ly + 2),
					fill: '#374151',
					'font-size': '12',
				},
				s.name ?? `시리즈${idx + 1}`,
			),
		)
		lx += 90
	})

	// 테두리
	root.appendChild(
		createEl('line', {
			x1: String(plotX),
			y1: String(plotY + 65),
			x2: String(plotX),
			y2: String(rectHeight - 45),
			stroke: '#6B6E79',
		}),
	)
	root.appendChild(
		createEl('line', {
			x1: String(plotX),
			y1: String(rectHeight - 45),
			x2: String(plotX + plotW),
			y2: String(rectHeight - 45),
			stroke: '#6B6E79',
		}),
	)

	mount.appendChild(svg)

	// ======= 내부 유틸 =======
	function createEl<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, text?: string): SVGElementTagNameMap[K] {
		const el = document.createElementNS(svgNS, tag) as SVGElementTagNameMap[K]
		Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v))
		if (text != null) el.textContent = text
		return el
	}

	function niceMax(v: number): number {
		const pow = Math.pow(10, Math.floor(Math.log10(v)))
		const n = v / pow
		const step = n <= 2 ? 2 : n <= 5 ? 5 : 10
		return step * pow
	}
	type Pt = [number, number] | null
	const pointsBySeries: Pt[][] = series.map(s =>
		Array.from({ length: xCount }, (_, i) => (typeof s.data[i].x === 'number' ? [xPointerScale(s.data[i].x as number), yPointerScale(s.data[i].y as number)] : null)),
	)

	pointsBySeries.forEach((pts, si) => {
		const color = series[si].color
		const fillColor: any = hexToRgb(color)
		pts.forEach(p => {
			if (!p) return
			root.appendChild(
				createEl('circle', {
					cx: String(p[0]),
					cy: String(p[1]),
					r: '4',
					fill: `rgba(${fillColor.r}, ${fillColor.g}, ${fillColor.b}, 0.5)`,
					stroke: `rgba(${fillColor.r}, ${fillColor.g}, ${fillColor.b}, 1)`,
					'stroke-width': '2',
				}),
			)
		})
	})

	function hexToRgb(hex: string) {
		if (!hex || !/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
			return null
		}
		let hexValue = hex.substring(1)
		if (hexValue.length === 3) {
			hexValue = hexValue
				.split('')
				.map(char => char + char)
				.join('')
		}
		const bigint = parseInt(hexValue, 16)
		return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 }
	}
}

/* ================= 예시 호출 =================
import { renderLineChartSVG } from "./lineChart";

renderLineChartSVG("#chart", {
  title: "Line chart name",
  xLabel: "(x축 레이블)",
  yLabel: "(y축 레이블)",
  xCategories: ["데이터 1", "데이터 2", "데이터 3", "데이터 4", "데이터 5"],
  series: [
    { name: "라벨1번", color: "#9CA3AF", data: [10, 6, 2, 7, 9] },
    { name: "라벨2번", color: "#22C1D6", data: [3, 5, 4, 8, 6] },
    { name: "라벨3번", color: "#F4C542", data: [5, 6, 1.5, 2, 3.5] },
    { name: "라벨4번", color: "#A78BFA", data: [11, 5.8, 9, 8.5, 10] }
  ],
  showArea: true
});
================================================ */
